/**
 * Test Data Control — Super-Admin-only sandbox tooling.
 * Two destructive actions guarded by AlertDialog confirmations.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/app/ui-bits";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Database, Trash, Sparkle, Warning, Buildings, UsersThree, Coins, Package, Handshake, ShieldCheck, ListChecks, ArrowsClockwise, FileText, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const SIZES = [
  { key: "small",  label: "Small Demo Data",         desc: "30 staff · 6 projects · 8 vendors · ~20 procurement records · ~25 assets" },
  { key: "medium", label: "Medium Company Demo Data", desc: "100 staff · 12 projects · 15 vendors · ~60 procurement records · ~80 assets · default" },
  { key: "large",  label: "Large Demo Data",         desc: "150 staff · 18 projects · 22 vendors · ~100 procurement records · ~120 assets" },
];

function InventoryGrid({ inv }) {
  if (!inv) return null;
  const buckets = [
    { key: "HR",          icon: UsersThree, prefixes: ["hr_"] },
    { key: "Finance",     icon: Coins,      prefixes: ["finance_", "invoice", "bank_", "donor_", "budget_", "fixed_", "depreciation", "tax_"] },
    { key: "Procurement", icon: Handshake,  prefixes: ["procurement", "vendor", "supplier"] },
    { key: "Projects",    icon: Buildings,  prefixes: ["project", "task", "deliverable"] },
    { key: "Assets",      icon: Package,    prefixes: ["asset"] },
    { key: "Compliance",  icon: ShieldCheck,prefixes: ["compliance"] },
    { key: "Misc",        icon: FileText,   prefixes: ["appointment", "calendar_", "meeting", "ethics_", "notifications"] },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7" data-testid="tdc-inventory">
      {buckets.map(({ key, icon: Icon, prefixes }) => {
        const count = Object.entries(inv.counts || {})
          .filter(([k]) => prefixes.some((p) => k.startsWith(p)))
          .reduce((s, [, v]) => s + v, 0);
        return (
          <div key={key} className="dc-card p-3" data-testid={`tdc-bucket-${key.toLowerCase()}`}>
            <div className="flex items-center gap-1.5">
              <Icon size={14} className="text-slate-400" />
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{key}</div>
            </div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-slate-900">{count}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function TestDataControl() {
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [size, setSize] = useState("medium");
  const [clearFirst, setClearFirst] = useState(true);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/test-data/inventory");
      setInv(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    if (user && user.role === "super_admin") refresh();
  }, [user]);

  // Frontend hard-gate: only super_admin can use this page. Mirrors
  // backend _require_super_admin. Declared AFTER all hooks to obey the
  // React rules of hooks.
  if (user && user.role !== "super_admin") {
    return <Navigate to="/" replace />;
  }

  const onClear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/test-data/clear");
      setLast({ kind: "cleared", ...data });
      toast.success(`Cleared ${data.total_deleted} records across ${Object.keys(data.by_collection).length} collections`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onSeed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/test-data/seed?size=${size}&clear_first=${clearFirst}`);
      setLast({ kind: "seeded", ...data });
      toast.success(`Seeded ${data.size} demo dataset`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="test-data-control">
      <PageHeader
        eyebrow="Super-Admin · sandbox tooling"
        title="Test Data Control"
        description="Manage demo / test data across the system. Use Clear to wipe operational data; use Seed to populate realistic interconnected records. User accounts, role assignments, settings, branding, and audit logs are always preserved."
      />

      {/* Banner */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="tdc-banner">
        <div className="flex items-start gap-2">
          <Warning size={18} weight="fill" className="text-amber-600 mt-0.5" />
          <div>
            <div className="font-semibold">This section is for Super Admin testing and demo data control only.</div>
            <div className="text-xs mt-1">Disable or hide before final production hand-over. All actions here are audit-logged.</div>
          </div>
        </div>
      </div>

      {/* Inventory */}
      <section>
        <div className="flex items-end justify-between gap-3 mb-2">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Current data snapshot</h2>
            <p className="text-xs text-slate-500">Live counts across operational collections. Refresh to recompute.</p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="tdc-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
        </div>
        <InventoryGrid inv={inv} />
        {inv && (
          <div className="mt-2 text-xs text-slate-500">
            Total records under management: <strong className="text-slate-700 font-mono-alt tabular-nums">{inv.total}</strong>
          </div>
        )}
      </section>

      {/* Clear */}
      <section className="dc-card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Trash size={22} className="text-rose-600 shrink-0" />
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Clear All Test Data</h2>
            <p className="text-sm text-slate-600">Removes all operational records from HR, Finance, Procurement, Projects, Tasks, Assets, Compliance, Ethics, Appointments, Calendar, Meetings, Notifications, and vendor onboarding. <strong>User accounts and system settings remain.</strong></p>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" disabled={busy} data-testid="tdc-clear-btn">
              <Trash size={14} className="mr-1" /> Clear All Test Data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear all test data?</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete all demo/test data across the system. User accounts and system settings will remain. Are you sure?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tdc-clear-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onClear} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="tdc-clear-confirm">
                Yes, clear everything
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      {/* Seed */}
      <section className="dc-card p-5 space-y-4">
        <div className="flex items-start gap-3">
          <Sparkle size={22} className="text-[#1E5CBA] shrink-0" />
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Seed Realistic Company Demo Data</h2>
            <p className="text-sm text-slate-600">Creates interconnected demo records mimicking a real medium-sized engineering and consulting firm. Names skew ~90 % Somali and ~10 % Kenyan / regional. Employees are linked to departments and projects; tasks to projects and employees; procurement to projects and vendors; finance entries to projects.</p>
          </div>
        </div>
        <div>
          <Label className="text-xs">Choose a preset</Label>
          <RadioGroup value={size} onValueChange={setSize} className="mt-2 space-y-2" data-testid="tdc-size-group">
            {SIZES.map((s) => (
              <label key={s.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${size === s.key ? "border-[#1E5CBA] bg-[#1E5CBA]/5" : "border-slate-200 hover:border-slate-300"}`}>
                <RadioGroupItem value={s.key} id={`size-${s.key}`} data-testid={`tdc-size-${s.key}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900">{s.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={clearFirst} onCheckedChange={(v) => setClearFirst(!!v)} data-testid="tdc-clear-first" />
          Clear existing test data before seeding (recommended)
        </label>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="btn-primary-blue" disabled={busy} data-testid="tdc-seed-btn">
              <Sparkle size={14} className="mr-1" /> Seed {size} demo dataset
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Seed {size} demo dataset?</AlertDialogTitle>
              <AlertDialogDescription>
                {clearFirst
                  ? "Existing operational data will be cleared FIRST, then a fresh interconnected demo dataset will be inserted."
                  : "A new demo dataset will be ADDED on top of existing data. Some records may duplicate."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tdc-seed-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onSeed} className="btn-primary-blue" data-testid="tdc-seed-confirm">
                Yes, seed now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>

      {/* Last operation result */}
      {last && (
        <section className="dc-card p-4 text-xs" data-testid="tdc-last-result">
          <div className="font-display text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <ListChecks size={14} className="text-slate-400" />
            Last operation · {last.kind === "cleared" ? `cleared ${last.total_deleted} records` : `seeded ${last.size} dataset`}
          </div>
          <pre className="whitespace-pre-wrap text-[11px] font-mono-alt text-slate-700 bg-slate-50 rounded p-3 max-h-64 overflow-auto">
            {JSON.stringify(last, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}
